
import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import {PwaInstallService} from "./pwa-install.service";
import {firstValueFrom} from "rxjs";
import {settings} from "../../settings/settings";
import {Router} from "@angular/router";
import * as AuthSelectors from "../../state/auth/auth.selector";
import {Store} from "@ngrx/store";

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
    private API_URL: string;
    private vapidPublicKey: string | null = null;

    constructor(
        private swPush: SwPush,
        private http: HttpClient,
        private router: Router,
        private store: Store
    ) {
        let httpProto = "http"
        if (settings.apiTls) {
            httpProto = "https"
        }
        this.API_URL = `${httpProto}://${settings.apiHost}:${settings.apiPort}/notifications/push`

        this.store.select(AuthSelectors.selectUser)
            .subscribe(async (user) => {
                if (user) {
                    await this.requestSubscription(user.id)
                }
            })
    }

    private async getVapidPublicKey(): Promise<string> {
        if (!this.vapidPublicKey) {
            const response = await firstValueFrom(
                this.http.get<{ publicKey: string }>(`${this.API_URL}/vapid-public-key`)
            );
            this.vapidPublicKey = response.publicKey;
        }
        return this.vapidPublicKey;
    }

    async requestSubscription(userId: string): Promise<void> {
        if (!this.swPush.isEnabled) {
            console.warn('Service Worker not enabled');
            return;
        }
        try {
            const publicKey = await this.getVapidPublicKey();
            const subscription = await this.swPush.requestSubscription({
                serverPublicKey: publicKey
            });
            // Send subscription to backend
            let data = subscription.toJSON() as any
            data.userId = userId
            await firstValueFrom(
                this.http.post(`${this.API_URL}/subscribe`, data)
            );
            console.log('Subscription sent to server');
            this.listenToNotifications()
        } catch (error) {
            console.error('Could not subscribe to notifications', error);
            throw error;
        }
    }

    async unsubscribe(): Promise<void> {
        try {
            const subscription = await this.swPush.subscription.pipe().toPromise();
            if (subscription) {
                await firstValueFrom(
                    this.http.post(`${this.API_URL}/unsubscribe`, subscription.toJSON())
                );
                await subscription.unsubscribe();
                console.log('Unsubscribed from notifications');
            }
        } catch (error) {
            console.error('Could not unsubscribe', error);
        }
    }

    listenToNotifications(): void {
        this.swPush.messages.subscribe((message: any) => {
            console.log('Received push notification:', message);
            // Handle notification data here
            // You can trigger UI updates, show in-app notifications, etc.
        });

        this.swPush.notificationClicks.subscribe((click: any) => {
            console.log('Notification clicked:', click);
            // Navigate to specific URL if provided in notification
            if (click.notification.data?.url) {
                // window.open(click.notification.data.url, '_blank');
                this.router.navigateByUrl(click.notification.data.url, {skipLocationChange: true});
            }
        });
    }

    get isSubscribed(): Promise<boolean> {
        return this.swPush.subscription.toPromise().then(sub => !!sub);
    }
}